import { all, call, put, takeLatest, delay } from 'redux-saga/effects';
import { digitalMarketingActions as actions } from '.';
import { PayloadAction } from '@reduxjs/toolkit';
import { blogApi } from 'features/DigitalMarketing/api/blogApi';

function* fetchBlogs(action: PayloadAction<{ institute_id: number; branch_id: number; publish_filter?: string; page?: number; page_size?: number; search?: string }>) {
  try {
    const response = yield call(blogApi.fetchBlogs, action.payload);
    const data = response?.data?.data;
    const items = Array.isArray(data)
      ? data
      : data?.Blogs || data?.BLOGS || data?.blogs || data?.items || [];
    yield put(actions.fetchBlogsSuccess({
      items,
      total_count: Array.isArray(data) ? data.length : data?.Total || data?.TOTAL || data?.total || data?.total_count || 0,
      has_next: Array.isArray(data) ? false : data?.has_next || false,
    }));
  } catch (error: any) {
    const errorDetails = error?.response?.message || error?.message;
    yield put(actions.fetchBlogsFailure(errorDetails));
  }
}

function* fetchBlogById(action: PayloadAction<{ id: number }>) {
  try {
    const response = yield call(blogApi.fetchBlogById, action.payload.id);
    const data = response?.data?.data;
    yield put(actions.fetchBlogByIdSuccess(data));
  } catch (error: any) {
    const errorDetails = error?.response?.message || error?.message;
    yield put(actions.fetchBlogByIdFailure(errorDetails));
  }
}

function* upsertBlog(action: PayloadAction<any>) {
  try {
    const response = yield call(blogApi.upsertBlog, action.payload);
    yield put(actions.upsertBlogSuccess(response?.data?.data));
  } catch (error: any) {
    const errorDetails = error?.response?.message || error?.message;
    yield put(actions.upsertBlogFailure(errorDetails));
  }
}

function* publishBlog(action: PayloadAction<{ blog_id: number; is_published: boolean; Authorization?: string }>) {
  try {
    const { blog_id } = action.payload;
    const response = yield call(blogApi.publishBlog, blog_id);
    yield put(actions.publishBlogSuccess(response?.data?.data));
  } catch (error: any) {
    const errorDetails = error?.response?.message || error?.message;
    yield put(actions.publishBlogFailure(errorDetails));
  }
}

function* deleteBlog(action: PayloadAction<{ id: number; Authorization?: string }>) {
  try {
    const response = yield call(blogApi.deleteBlog, action.payload.id);
    yield put(actions.deleteBlogSuccess(response?.data?.data));
  } catch (error: any) {
    const errorDetails = error?.response?.message || error?.message;
    yield put(actions.deleteBlogFailure(errorDetails));
  }
}

function* generateAIText(action: PayloadAction<{ title: string; description: string }>) {
  try {
    const response = yield call(blogApi.generateAIText, action.payload);
    yield put(actions.generateAITextSuccess(response?.data?.data));
  } catch (error: any) {
    const errorDetails = error?.response?.message || error?.message;
    yield put(actions.generateAITextFailure(errorDetails));
  }
}

function* generateAIImage(action: PayloadAction<{ title: string; description: string }>) {
  try {
    const response = yield call(blogApi.generateAIImage, action.payload);
    let taskId = response?.data?.data?.taskId || response?.data?.data;

    if (typeof taskId === "object" && taskId?.task_id) {
      taskId = taskId.task_id;
    }

    yield put(actions.generateAIImageTaskSuccess(taskId));

    let isReady = false;
    let attempts = 0;
    while (!isReady && attempts < 20) {
      yield delay(3000);
      attempts++;
      try {
        const imgRes = yield call(blogApi.getGeneratedImage, taskId);
        const imageUrl = imgRes?.data?.data;
        
        if (imageUrl && typeof imageUrl === "string" && imageUrl.trim().length > 0 && imageUrl !== "pending") {
          yield put(actions.generateAIImageSuccess(imageUrl));
          isReady = true;
        }
      } catch (e: any) {
        if (attempts >= 20 || e?.response?.status === 500) {
          throw e;
        }
      }
    }
    if (!isReady) {
      yield put(actions.generateAIImageFailure({ message: "Image generation timed out" } as any));
    }
  } catch (error: any) {
    const errorDetails = error?.response?.message || error?.message;
    yield put(actions.generateAIImageFailure(errorDetails));
  }
}

function* enhanceImage(action: PayloadAction<{ user_image_url: string }>) {
  try {
    const payload = {
      user_image_url: action.payload.user_image_url,
      face_upsample: true,
      background_enhance: true,
      upscale: 2,
      fidelity_weight: 0.5
    };
    const response = yield call(blogApi.enhanceImage, payload);
    let taskId = response?.data?.data?.taskId || response?.data?.data;

    if (typeof taskId === "object" && taskId?.task_id) {
      taskId = taskId.task_id;
    }

    let isReady = false;
    let attempts = 0;
    while (!isReady && attempts < 20) {
      yield delay(3000);
      attempts++;
      try {
        const imgRes = yield call(blogApi.getGeneratedImage, taskId);
        const imageUrl = imgRes?.data?.data;
        
        if (imageUrl && typeof imageUrl === "string" && imageUrl.trim().length > 0 && imageUrl !== "pending") {
          yield put(actions.enhanceImageSuccess(imageUrl));
          isReady = true;
        }
      } catch (e: any) {
        if (attempts >= 20 || e?.response?.status === 500) {
          throw e;
        }
      }
    }
    if (!isReady) {
      yield put(actions.enhanceImageFailure({ message: "Image enhancement timed out" } as any));
    }
  } catch (error: any) {
    const errorDetails = error?.response?.message || error?.message;
    yield put(actions.enhanceImageFailure(errorDetails));
  }
}

export function* digitalMarketingSaga() {
  yield all([
    takeLatest(actions.fetchBlogsStart.type, fetchBlogs),
    takeLatest(actions.fetchBlogByIdStart.type, fetchBlogById),
    takeLatest(actions.upsertBlogStart.type, upsertBlog),
    takeLatest(actions.publishBlogStart.type, publishBlog),
    takeLatest(actions.deleteBlogStart.type, deleteBlog),
    takeLatest(actions.generateAITextStart.type, generateAIText),
    takeLatest(actions.generateAIImageStart.type, generateAIImage),
    takeLatest(actions.enhanceImageStart.type, enhanceImage),
  ]);
}
